import React from 'react';
import { Helmet } from 'react-helmet';
import SimpleHero from '../../components/SimpleHero/SimpleHero';
import FeatureList from '../../components/Minimal/FeatureList';
import bank from '../../assets/images/patriot.png';
import './PatriotAct.css';

const PatriotAct = () => {
  // Get the current URL for sharing
  const currentUrl = typeof window !== 'undefined' 
    ? window.location.origin + window.location.pathname
    : 'https://gnbank.net/PatriotAct';
  
  // OG image URL for social sharing
  // Recommended: Create a 1200x630px image and save it as public/patriot-og.png
  // For now using the default OG image - update this path once you add patriot-og.png to public folder
  const ogImageUrl = 'https://gnbank.net/patriot-og.png';

  return (
    <div>
      <Helmet>
        <title>USA PATRIOT Act Notice - Identity Verification Requirements | GN Bank</title>
        <meta 
          name="description" 
          content="Federal law requires all financial institutions to obtain, verify, and record information that identifies each person who opens an account. Learn about GN Bank's USA PATRIOT Act compliance and identity verification requirements." 
        />
        
        {/* Open Graph / Facebook / LinkedIn */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content="USA PATRIOT Act Notice - Identity Verification Requirements | GN Bank" />
        <meta 
          property="og:description" 
          content="Federal law requires all financial institutions to obtain, verify, and record information that identifies each person who opens an account. Learn about GN Bank's USA PATRIOT Act compliance." 
        />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="GN Bank" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content="USA PATRIOT Act Notice - Identity Verification Requirements | GN Bank" />
        <meta 
          name="twitter:description" 
          content="Federal law requires all financial institutions to obtain, verify, and record information that identifies each person who opens an account." 
        />
        <meta name="twitter:image" content={ogImageUrl} />
        
        {/* Additional meta tags */}
        <meta name="keywords" content="USA PATRIOT Act, identity verification, KYC, AML, BSA, GN Bank, compliance, federal banking regulations" />
        <link rel="canonical" href={currentUrl} />
      </Helmet>
      <SimpleHero
        title="USA PATRIOT Act Notice"
        subtitle="Federal law requires all financial institutions to obtain, verify, and record information that identifies each person who opens an account to help fight terrorism and money laundering."
        variant="sofi"
        imageSrc={bank}
        imageAlt="USA PATRIOT Act"
        imageOn="right"
        tiltImage
        badges={["Identity Verification", "Federal Compliance", "Customer Protection"]}
      />
      <FeatureList
        items={[
          { title: 'Identity Verification', desc: 'Required information to open and maintain accounts.' },
          { title: 'Federal Compliance', desc: 'Compliance with USA PATRIOT Act and BSA regulations.' },
          { title: 'Customer Protection', desc: 'Safeguarding your information and the financial system.' },
        ]}
      />
      <section className="section">
        <div className="gn-container">
          <div className="patriot-act-content">
            <p className="patriot-act-intro">
              To help the government fight the funding of terrorism and money laundering activities, Federal law requires all financial institutions to obtain, verify, and record information that identifies each person who opens an account.
            </p>
            <p className="patriot-act-intro">
              This notice describes our obligations under the USA PATRIOT Act, and your responsibilities as a customer of GN Bank.
            </p>

            <div className="patriot-act-section">
              <h3>1. Information We Must Collect</h3>
              <p>When you open an account, we are required by federal law to ask for identifying information such as:</p>
              <ul>
                <li>Your full legal name</li>
                <li>Residential address</li>
                <li>Date of birth</li>
                <li>Social Security Number or Taxpayer Identification Number</li>
                <li>Government-issued photo identification (e.g., driver's license, passport)</li>
              </ul>
              <p>For businesses, we may collect:</p>
              <ul>
                <li>Legal business name and address</li>
                <li>Employer Identification Number (EIN)</li>
                <li>Ownership structure</li>
                <li>Beneficial owners (per FinCEN's BOI rule)</li>
                <li>Authorized signers and controlling persons</li>
              </ul>
            </div>

            <div className="patriot-act-section">
              <h3>2. Additional Verification</h3>
              <p>To comply with federal regulations and protect customers, we may require additional identity verification, including but not limited to:</p>
              <ul>
                <li>Document uploads (ID, utility bill, etc.)</li>
                <li>Biometric verification (selfie match / liveness check)</li>
                <li>Credit bureau information</li>
                <li>Public records</li>
                <li>Phone number verification</li>
                <li>IP/geolocation checks</li>
                <li>Employer or income verification</li>
                <li>OFAC screening</li>
              </ul>
              <p>These steps help ensure your identity is legitimate and help protect the financial system from illicit activity.</p>
            </div>

            <div className="patriot-act-section">
              <h3>3. When Identification Cannot Be Verified</h3>
              <p>If we are unable to verify your identity:</p>
              <ul>
                <li>Your account may not be opened; or</li>
                <li>Your existing account may be suspended or closed; and</li>
                <li>Transactions may be delayed or restricted</li>
              </ul>
              <p>These actions are mandated by federal law for the safety of the banking system.</p>
            </div>

            <div className="patriot-act-section">
              <h3>4. Ongoing Monitoring & Compliance</h3>
              <p>Under the Bank Secrecy Act (BSA) and the USA PATRIOT Act, we must:</p>
              <ul>
                <li>Monitor activity for suspicious behavior</li>
                <li>File required reports (SARs/CTRs) with FinCEN when appropriate</li>
                <li>Maintain transaction records</li>
                <li>Conduct periodic re-verification of customer information</li>
              </ul>
              <p>We may contact you for updated documents or information at any time.</p>
            </div>

            <div className="patriot-act-section">
              <h3>5. No Opt-Out</h3>
              <p>These identity verification requirements are not optional. Federal law mandates that all customers provide accurate and complete information.</p>
              <p>Failure to comply may result in:</p>
              <ul>
                <li>Delayed services</li>
                <li>Account closure</li>
                <li>Restrictions on transactions</li>
              </ul>
            </div>

            <div className="patriot-act-section">
              <h3>6. Protection of Your Information</h3>
              <p>All collected information is used solely to comply with:</p>
              <ul>
                <li>The USA PATRIOT Act</li>
                <li>BSA/AML regulations</li>
                <li>OFAC sanctions rules</li>
                <li>Applicable federal and state banking laws</li>
              </ul>
              <p>Your information is safeguarded in accordance with:</p>
              <ul>
                <li>GLBA (Gramm–Leach–Bliley Act)</li>
                <li>Our Privacy Policy</li>
                <li>FFIEC data security guidelines</li>
              </ul>
              <p>We do not sell customer identity information.</p>
            </div>

            <div className="patriot-act-section">
              <h3>7. Questions</h3>
              <p>If you have any questions about this disclosure or identity verification:</p>
              <p>
                GN Bank Compliance Department<br />
                4619 S. King Drive, Chicago, IL 60653<br />
                +1 (773) 624-2000<br />
                https://gnbank.net
              </p>
            </div>

            <div className="patriot-act-footer">
              <p>We appreciate your cooperation in helping us adhere to federal requirements designed to protect our customers and the financial system.</p>
            </div>

            <div className="patriot-act-cta text-center">
              <a className="btn btn-gn-primary" href="/contact">Contact Compliance</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PatriotAct;
